import React from "react";
import { Link } from "react-router-dom";
import {
  Scale, Gavel, Briefcase, Home, Shield, ShieldCheck, Users, Coins,
  Scroll, Handshake, HeartPulse, Heart, HeartHandshake, Split,
  FileSignature, DollarSign,
} from "lucide-react";
import { SERVICES, serviceUrl, serviceTitle, UI } from "@/content/site";

// Semantic lucide-react icon mapping per service key.
const ICON_COMPONENTS = {
  "divorce": Scale,
  "contested-divorce": Gavel,
  "high-asset-divorce": Briefcase,
  "property-assets": Home,
  "domestic-violence": Shield,
  "restraining-orders": ShieldCheck,
  "child-custody": Users,
  "child-support": Coins,
  "wills-and-trusts": Scroll,
  "grandparents-stepparent-adoptions": Handshake,
  "assisted-reproduction": HeartPulse,
  "common-law-marriages": Heart,
  "same-sex-divorce": HeartHandshake,
  "legal-separation": Split,
  "prenuptial-postnuptial": FileSignature,
  "alimony": DollarSign,
};

const DESCRIPTIONS = {
  en: {
    "divorce": "Guidance through Dallas County divorce and legal separation processes.",
    "contested-divorce": "Skilled representation for high-conflict, contested, and disputed divorces.",
    "high-asset-divorce": "Complex property and financial division for high-net-worth divorces.",
    "property-assets": "Division of community and separate property assets during divorce.",
    "domestic-violence": "Protection orders and legal action for victims of family violence.",
    "restraining-orders": "Fast legal action for your safety and protection in Dallas County.",
    "child-custody": "Secure custody and visitation rights that put your children first.",
    "child-support": "Calculate, modify, and enforce Texas child support obligations.",
    "wills-and-trusts": "Essential estate planning to protect your family and assets.",
    "grandparents-stepparent-adoptions": "Legal adoption process for grandparents, stepparents, and international adoptions.",
    "assisted-reproduction": "Legal guidance on surrogacy, egg/sperm donation, and fertility agreements.",
    "common-law-marriages": "Divorce and property division for Texas informal marriages.",
    "same-sex-divorce": "Custody and asset division tailored to same-sex and LGBTQ+ couples.",
    "legal-separation": "For couples choosing to separate with or without divorce.",
    "prenuptial-postnuptial": "Protect your financial assets before or after marriage.",
    "alimony": "Fair spousal support agreements after separation or divorce.",
  },
  es: {
    "divorce": "Orientación a través de los procesos de divorcio y separación legal.",
    "contested-divorce": "Representación calificada en divorcios muy conflictivos y contenciosos.",
    "high-asset-divorce": "Manejo experto de divisiones patrimoniales y financieras complejas.",
    "property-assets": "División justa de bienes gananciales y separados en el divorcio.",
    "domestic-violence": "Órdenes de protección y acciones legales para víctimas de abuso.",
    "restraining-orders": "Acción legal rápida para su seguridad y protección.",
    "child-custody": "Aseguramos derechos de custodia y visitas centrados en sus hijos.",
    "child-support": "Calcular, modificar y hacer cumplir la manutención de hijos.",
    "wills-and-trusts": "Planificación patrimonial esencial para proteger a su familia.",
    "grandparents-stepparent-adoptions": "Proceso legal de adopción para abuelos, padrastros y adopciones internacionales.",
    "assisted-reproduction": "Orientación sobre gestación subrogada y acuerdos de fertilidad.",
    "common-law-marriages": "Divorcio y división de bienes para matrimonios informales en Texas.",
    "same-sex-divorce": "Custodia y división de bienes para parejas del mismo sexo.",
    "legal-separation": "Para parejas que deciden separarse con o sin divorcio.",
    "prenuptial-postnuptial": "Proteja sus bienes antes o después del matrimonio.",
    "alimony": "Acuerdos justos de pensión alimenticia después del divorcio.",
  },
};

export default function ServiceGrid({ heading, lang }) {
  const ui = UI[lang];
  const desc = DESCRIPTIONS[lang];
  return (
    <div>
      {heading && <>
        <span className="gw-eyebrow">{lang === "en" ? "Practice Areas" : "Áreas de Práctica"}</span>
        <h2 style={{maxWidth: 720, marginBottom: "var(--gw-space-8)"}}>{heading}</h2>
      </>}
      <div className="gw-service-grid">
        {SERVICES.map((s) => {
          const Icon = ICON_COMPONENTS[s.key] || Scale;
          return (
            <Link key={s.key} to={serviceUrl(s, lang)} className="gw-service-card gw-card gw-card-link">
              <div className="gw-service-icon" aria-hidden="true">
                <Icon size={28} strokeWidth={1.75} />
              </div>
              <h3>{serviceTitle(s, lang)}</h3>
              <p>{desc[s.key]}</p>
              <span className="gw-service-cta">{ui.ctaLearnMore} →</span>
            </Link>
          );
        })}
      </div>
      <style>{`
        .gw-service-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: var(--gw-space-5);
        }
        .gw-service-card {
          padding: var(--gw-space-5);
          display: flex; flex-direction: column;
          border: 1px solid var(--gw-border);
          transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
          height: 100%;
        }
        .gw-service-card:hover {
          border-color: var(--gw-primary);
          text-decoration: none;
          color: inherit;
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(59, 10, 20, 0.10);
        }
        .gw-service-card:hover .gw-service-icon {
          background: linear-gradient(135deg, var(--gw-primary) 0%, var(--gw-primary-dark) 100%);
          color: var(--gw-accent);
        }
        .gw-service-icon {
          width: 56px; height: 56px;
          display: inline-flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #F5EFE3 0%, #E8DFCB 100%);
          border-radius: 14px;
          color: var(--gw-primary);
          margin-bottom: var(--gw-space-4);
          transition: background 0.22s ease, color 0.22s ease;
        }
        .gw-service-card h3 { font-size: 1.15rem; margin-bottom: var(--gw-space-3); }
        .gw-service-card p { color: var(--gw-text-soft); font-size: 0.95rem; margin-bottom: var(--gw-space-4); flex-grow: 1; }
        .gw-service-cta { color: var(--gw-primary); font-weight: 600; font-size: 0.9rem; margin-top: auto; }
      `}</style>
    </div>
  );
}
